<template>
    <DModal
        :show="showModal"
        :title="modalTitle"
        is-big
        overflow-auto
        @close="closeModal"
    >
        <div
            v-if="emailLoading"
            class="loading-box"
        >
            <DIcon
                name="loading"
                class="loading"
                :size="40"
            />
        </div>

        <div
            v-if="!emailLoading"
            class="modal-base"
        >
            <div
                v-if="errors.length === 0"
                class="modal-header"
            >
                <DTabs>
                    <DTabItem
                        :label="__('tools.emails.modal.tabs.html')"
                        :active="currentTab === 0"
                        @click="changeTab(0)"
                    />
                    <DTabItem
                        :label="__('tools.emails.modal.tabs.plain_text')"
                        :active="currentTab === 1"
                        @click="changeTab(1)"
                    />
                    <DTabItem
                        :label="__('tools.emails.modal.tabs.send_copy')"
                        :active="currentTab === 2"
                        @click="changeTab(2)"
                    />
                </DTabs>
                <DButton
                    v-if="emailArchive.eml"
                    icon="download"
                    :label="__('customers.communications.modal.download')"
                    :disabled="isInLoadingState"
                    @click="downloadSampleDocument()"
                />
            </div>

            <DAlert
                v-if="errors.length > 0"
                color="danger"
                class="d-flex align-items-center"
            >
                <DIcon
                    name="alert"
                    :size="24"
                />
                <div class="pr-2">
                    <p
                        v-for="(error, index) in errors"
                        :key="index"
                        class="mb-0"
                    >
                        {{ error }}
                    </p>
                </div>
            </DAlert>

            <div v-if="errors.length === 0">
                <DCard
                    v-if="currentTab === 0"
                    class="ignore-parent-styles"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div v-html="emailArchive.html" />
                    <!--eslint-enable-->
                </DCard>

                <DCard
                    v-if="currentTab === 1"
                    padding
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        class="raw-textbox"
                        v-html="emailArchive.text"
                    />
                    <!--eslint-enable-->
                </DCard>

                <DCard
                    v-if="currentTab === 2 && customer !== null"
                    padding
                >
                    <div class="mt-3">
                        <DInput
                            id="email"
                            v-model="email"
                            :label="__('tools.emails.modal.email')"
                            add-label-classes="required"
                        />
                        <div class="d-flex justify-content-start align-items-center">
                            <DButton
                                :loading="sendingEmailLoading"
                                :label="__('customers.communications.modal.tabs.send_copy')"
                                :disabled="!email || isInLoadingState"
                                :color="'primary'"
                                @click="sendEmail()"
                            />
                            <p
                                v-if="mailSentSuccessfully"
                                class="text-success ms-3 mb-0 p-0"
                            >
                                {{ __('tools.emails.modal.mail_sent') }}
                            </p>
                        </div>
                    </div>
                </DCard>
            </div>
        </div>
    </DModal>
</template>

<script setup>

import { ref, watch } from 'vue'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    emailId: {
        type: Number,
        default: 0
    },
    customer: {
        type: Object,
        default: () => { return {} }
    }
})

const emit = defineEmits(['closeModal'])

const emailArchive = ref({})
const modalTitle = ref(__('messages.loading'))
const emailLoading = ref(true)
const sendingEmailLoading = ref(false)
const mailSentSuccessfully = ref(false)
const errors = ref([])
const email = ref(props.customer?.email ?? '')
const currentTab = ref(0)

watch(
    () => props.showModal,
    (value) => {
        if (value === true) {
            loadEmailData()
        } else {
            closeModal()
        }
    }
)

const changeTab = (tabIndex) => {
    currentTab.value = tabIndex
}

const loadEmailData = () => {
    emailLoading.value = true
    emailArchive.value = {}
    errors.value = []
    window.axios.get(route('tools.communications.email-archive', {
        emailArchive: props.emailId
    }))
        .then(response => {
            emailArchive.value = response.data
            modalTitle.value = emailArchive.value.subject
        })
        .catch((error) => {
            parseErrors(error)
        })
        .finally(() => {
            emailLoading.value = false
        })
}

const downloadSampleDocument = () => {
    const link = document.createElement('a')
    link.download = emailArchive.value.fileName
    const blob = new Blob([emailArchive.value.eml], {
        type: 'text/plain'
    })
    link.href = window.URL.createObjectURL(blob)
    link.click()
}

const sendEmail = () => {
    sendingEmailLoading.value = true
    mailSentSuccessfully.value = false
    errors.value = []

    window.axios.post(route('tools.communications.resend-email', {
        customer: props.customer.id,
        emailArchive: props.emailId
    }), {
        email: email.value
    })
        .then(() => {
            mailSentSuccessfully.value = true
        })
        .catch((error) => {
            parseErrors(error)
        })
        .finally(() => {
            sendingEmailLoading.value = false
        })
}

const closeModal = () => {
    errors.value = []
    email.value = props?.customer?.email ?? ''
    modalTitle.value = __('messages.loading')
    mailSentSuccessfully.value = false
    sendingEmailLoading.value = false
    emailLoading.value = true
    currentTab.value = 0

    emit('closeModal')
}

const parseErrors = (error) => {
    const errorObject = error.response && error.response.data && error.response.data.errors
        ? error.response.data.errors
        : {}
    Object.keys(errorObject).forEach((field) => {
        errorObject[field].forEach((message) => {
            errors.value.push(message)
        })
    })
}

</script>
<style lang="scss" scoped>
.modal-base {
    margin: 15px;
    display: grid;
    grid-gap: 15px;

    & .modal-header {
        display: grid;
        grid-gap: 15px;
        grid-template-columns: 1fr auto;
        align-items: center;
    }
    & .raw-textbox {
        font-family: monospace, monospace;
        white-space: pre-wrap;
    }
}

.loading-box {
    height: 250px;
    display: flex;
    justify-content: center;
    align-items: center;

    & .loading {
        animation: loading 0.4s linear infinite;
    }
}

@keyframes loading {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
